import React, { useState, useEffect } from "react";
import { X, Save, Loader2 } from "lucide-react";
import { brandService } from "../../lib/brandService";
import { toast } from "sonner";

const CreateBrandModal = ({
  isOpen,
  onClose,
  onSuccess,
  editBrand = null,
}) => {
  const slugify = (value) =>
    (value || "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: true,
    display_order: 0,
    productIds: [],
    brandKey: "",
  });
  const [allProducts, setAllProducts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [brandKeyTouched, setBrandKeyTouched] = useState(false);

  // Load products when modal opens
  useEffect(() => {
    if (isOpen) {
      loadProducts();
    }
  }, [isOpen]);

  // Set form data when editing
  useEffect(() => {
    if (editBrand) {
      setFormData({
        name: editBrand.name,
        description: editBrand.description || "",
        is_active: editBrand.is_active,
        display_order: editBrand.display_order,
        productIds: editBrand.products?.map((p) => p.id) || [],
        brandKey: editBrand.brand_key || "",
      });
      setBrandKeyTouched(true);
    } else {
      // Reset form for create mode
      setFormData({
        name: "",
        description: "",
        is_active: true,
        display_order: 0,
        productIds: [],
        brandKey: "",
      });
      setBrandKeyTouched(false);
    }
  }, [editBrand]);

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const result = await brandService.getAllProducts();
      if (result.success) {
        setAllProducts(result.data);
      } else {
        toast.error("Failed to load products");
      }
    } catch (error) {
      toast.error("Failed to load products");
      console.error("Error loading products:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Brand name is required");
      return;
    }

    if (!formData.brandKey.trim()) {
      toast.error("Brand key is required");
      return;
    }

    setIsSubmitting(true);
    try {
      let result;
      if (editBrand) {
        result = await brandService.updateBrand(editBrand.id, formData);
      } else {
        result = await brandService.createBrand(formData);
      }

      if (result.success) {
        toast.success(
          `Brand ${editBrand ? "updated" : "created"} successfully`
        );
        onSuccess();
        onClose();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error(`Failed to ${editBrand ? "update" : "create"} brand`);
      console.error("Error submitting brand:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProductToggle = (productId) => {
    setFormData((prev) => ({
      ...prev,
      productIds: prev.productIds.includes(productId)
        ? prev.productIds.filter((id) => id !== productId)
        : [...prev.productIds, productId],
    }));
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleNameChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      name: value,
      brandKey:
        !editBrand && !brandKeyTouched
          ? slugify(value)
          : prev.brandKey,
    }));
  };

  const handleBrandKeyChange = (value) => {
    setBrandKeyTouched(true);
    setFormData((prev) => ({
      ...prev,
      brandKey: slugify(value),
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-gray-900/95 border border-white/10 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">
            {editBrand ? "Edit Brand" : "Create Brand"}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Brand Name */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Brand Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
              placeholder="Enter brand name"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Brand Key */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Brand Key *{" "}
              <span className="text-xs text-gray-400">
                (used in data imports and APIs)
              </span>
            </label>
            <input
              type="text"
              value={formData.brandKey}
              onChange={(e) => handleBrandKeyChange(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
              placeholder="e.g. holistic_hercules"
              required
              disabled={isSubmitting || Boolean(editBrand)}
            />
            {!editBrand && (
              <p className="mt-2 text-xs text-gray-400">
                Generated from the name, but you can customize it to match your data feeds.
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              rows={3}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent resize-none"
              placeholder="Enter brand description"
              disabled={isSubmitting}
            />
          </div>

          {/* Products Selection */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Products ({formData.productIds.length} selected)
            </label>
            <div className="max-h-60 overflow-y-auto bg-white/5 border border-white/10 rounded-lg p-4">
              {loadingProducts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#B8460E]" />
                  <span className="ml-2 text-gray-400">Loading products...</span>
                </div>
              ) : allProducts.length === 0 ? (
                <p className="text-gray-400 text-center py-4">
                  No products available
                </p>
              ) : (
                <div className="space-y-2">
                  {allProducts.map((product) => (
                    <label
                      key={product.id}
                      className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.productIds.includes(product.id)}
                        onChange={() => handleProductToggle(product.id)}
                        disabled={isSubmitting}
                        className="w-4 h-4 text-[#B8460E] bg-white/10 border-white/20 rounded focus:ring-[#B8460E] focus:ring-2 disabled:opacity-50"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white">{product.name}</span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              product.is_active
                                ? "bg-green-500/20 text-green-400"
                                : "bg-red-500/20 text-red-400"
                            }`}
                          >
                            {product.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  is_active: e.target.checked,
                }))
              }
              disabled={isSubmitting}
              className="w-4 h-4 text-[#B8460E] bg-white/10 border-white/20 rounded focus:ring-[#B8460E] focus:ring-2 disabled:opacity-50"
            />
            <label htmlFor="is_active" className="text-white">
              Active Brand
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-white/10">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-6 py-3 text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {editBrand ? "Update Brand" : "Create Brand"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBrandModal;
